import React, { useEffect, useReducer } from 'react';
import { Spinner } from 'react-bootstrap';
import SocialLoginInterface from 'shared/api/SocialLoginInterface';
import SStart from 'shared/screens/Start';
import { ASSET_KEY, assetSource } from 'shared/utils/assetSource';
import audio from 'shared/utils/audio';
import ENVIRONMENT_VARIABLES from 'shared/utils/environmentVariables';
import { getText, TEXT_KEY } from 'shared/utils/localization';
import RouteParamConstants from 'shared/utils/routeParamConstants';
import { useTheme } from 'styled-components';
import Button from '../../components/Button/Button';
import TermsModal from '../../components/TermsModal';
import ResetPassword from '../ResetPassword';
import Signup from '../Signup';
import TermsModalWrapper from './../../components/TermsModal/TermsModalComponents/TermsModalWrapper';
import { BottomBarComponent } from './components/BottomBar';
import {
  GuestLoginButtonWrapper,
  LogoImage,
  MainContentContainer,
  MainContentHeader,
  SignInButtonImage,
  SignInButtonWrapper,
  SplashScreen,
  StartScreenWrapper,
} from './styledComponents';
import Styles from './styles';

const DefaultStart = props => {
  const { Start } = useTheme();

  const isGuestLoginButton = ENVIRONMENT_VARIABLES.PLAY_AS_GUEST_TYPE === 'BUTTON';

  const [state, setState] = useReducer(
    (prev, next) => {
      return { ...prev, ...next };
    },
    {
      oauthLoading: false,
      statusMessage: null,
      emailPermissionAccepted: null,
      socialError: null,
      socialErrorType: null,
      playAsGuest: ENVIRONMENT_VARIABLES.PLAY_AS_GUEST,
      error: null,
    }
  );

  useEffect(() => {
    if (props.navigation.getParam(RouteParamConstants.PARAM_ACTION)) {
      _handleActionRedirect();
    }
    if (props.navigation.getParam(RouteParamConstants.PARAM_TRACKING_URL_AFF_ID)) {
      let aff_Id = props.navigation.getParam(RouteParamConstants.PARAM_TRACKING_URL_AFF_ID);
      Array.isArray(aff_Id) ? props.setUserAffId(aff_Id[0]) : props.setUserAffId(aff_Id);
    }
  }, []);

  const _handleActionRedirect = () => {
    window.location.assign(props.navigation.getParam(RouteParamConstants.PARAM_ACTION));
  };

  useEffect(() => {
    props.googleToken &&
      SocialLoginInterface.googleLoginHandler({
        setSuccessState: setSuccessState,
        setErrorState: setErrorState,
        setCanceledState: setCanceledState,
      });
  }, [props.googleToken]);

  const setSuccessState = () => {
    setState({
      oauthLoading: true,
      statusMessage: null,
      socialError: null,
      socialErrorType: null,
      error: null,
    });
  };

  const setErrorState = () => {
    setState({
      oauthLoading: false,
      statusMessage: null,
      socialError: null,
      socialErrorType: null,
      error: true,
    });
  };

  const setCanceledState = ({ statusMessage, emailPermissionAccepted, socialErrorType }) => {
    setState({
      oauthLoading: false,
      statusMessage,
      emailPermissionAccepted,
      socialError: true,
      socialErrorType,
      error: null,
    });
  };

  const closeModal = () => {
    audio.onClick();
    setState({
      statusMessage: null,
      socialError: false,
      socialErrorType: null,
      error: null,
    });
  };

  return (
    <SStart
      navigation={props.navigation}
      mountIntent={{
        facebook: () =>
          SocialLoginInterface.facebookLoginHandler({
            FB,
            emailPermissionAccepted: state.emailPermissionAccepted,
            setSuccessState: setSuccessState,
            setErrorState: setErrorState,
            setCanceledState: setCanceledState,
          }),
      }}
      render={args => {
        const {
          onGuestActivate,
          onAccountActivate,
          onFacebookActivate,
          isVisible,
          terms,
          isLoading,
          closeModal: argsCloseModal,
          onSubmit,
          setTerms,
          termsAndConditionsUrl,
          privacyPolicyUrl,
          showSignUpForm,
          signupHandler,
          recoverPasswordResponse,
          hasFacebook,
        } = args;

        return (
          <SplashScreen className="SplashScreen">
            <Signup
              isModal
              isVisible={showSignUpForm}
              title={getText(TEXT_KEY.SIGNUP_TITLE)}
              closeModal={argsCloseModal}
              closeRetryModal={closeModal}
              zIndex={101}
              modalStyle={Styles.modalStyle}
              modalContainerStyle={Styles.modalContainerStyle}
              modalMainBodyStyle={Styles.modalMainBodyStyle}
              titleStyle={Styles.titleStyle}
              termsAndConditionsUrl={termsAndConditionsUrl}
              privacyPolicyUrl={privacyPolicyUrl}
              navigation={props.navigation}
            />
            <ResetPassword
              isVisible={recoverPasswordResponse.visible}
              closeModal={argsCloseModal}
              zIndex={101}
              navigation={props.navigation}
              invalid={recoverPasswordResponse.invalid}
              errorMessage={recoverPasswordResponse.message}
              recoverId={recoverPasswordResponse.id}
              recoverToken={recoverPasswordResponse.token}
              getTextFunc={getText}
              textKeys={TEXT_KEY}
              TermsModalWrapper={TermsModalWrapper}
            />
            <TermsModal
              isVisible={isVisible}
              closeModal={argsCloseModal}
              closeRetryModal={closeModal}
              terms={terms}
              setTerms={setTerms}
              onSubmit={onSubmit}
              onFacebookActivate={onFacebookActivate}
              error={state.error}
              socialError={state.socialError}
              socialErrorType={state.socialErrorType}
              statusMessage={state.statusMessage}
              clientText={getText(TEXT_KEY.CLIENT_TITLE)}
              termsAndConditionsUrl={termsAndConditionsUrl}
              privacyPolicyUrl={privacyPolicyUrl}
            />
            <StartScreenWrapper>
              <LogoImage applyTheme={Start.Logo} src={assetSource(ASSET_KEY.START_SCREEN_LOGO)} />
              <MainContentContainer guestButton={isGuestLoginButton} theme={Start.MainContentContainer}>
                <MainContentHeader>{getText(TEXT_KEY.WELCOME_START)}</MainContentHeader>
                <SignInButtonWrapper className="splash-signup-button" onClick={signupHandler}>
                  <SignInButtonImage src={assetSource(ASSET_KEY.GUEST_BUTTON)} />
                </SignInButtonWrapper>
                {(hasFacebook || (state.playAsGuest === 'true' && isGuestLoginButton)) && (
                  <MainContentHeader>{getText(TEXT_KEY.OR)}</MainContentHeader>
                )}
                {hasFacebook && (
                  <SignInButtonWrapper className="splash-facebook-button" onClick={onFacebookActivate}>
                    <SignInButtonImage src={assetSource(ASSET_KEY.FACEBOOK_BUTTON)} />
                  </SignInButtonWrapper>
                )}
                {state.playAsGuest === 'true' && isGuestLoginButton && (
                  <GuestLoginButtonWrapper>
                    <Button
                      imageSource={assetSource(ASSET_KEY.LOGIN_GUEST_BUTTON)}
                      onClick={onGuestActivate}
                      containerStyle={Styles.guestButtonStyle}
                      imageStyle={Styles.guestButtonStyle}
                      objectFit="contain"
                    />
                  </GuestLoginButtonWrapper>
                )}
              </MainContentContainer>
              <BottomBarComponent
                onAccountActivate={onAccountActivate}
                withGuest={state.playAsGuest === 'true'}
                isGuestLoginButton={isGuestLoginButton}
                onGuestActivate={onGuestActivate}
              />
            </StartScreenWrapper>
            {isLoading && !state.oauthLoading && (
              <Spinner style={{ position: 'absolute' }} animation="grow" className="custom-spinner" />
            )}
            {state.oauthLoading && !isLoading && (
              <Spinner style={{ position: 'absolute' }} animation="grow" className="custom-spinner" />
            )}
          </SplashScreen>
        );
      }}
    />
  );
};

DefaultStart.path = '';

export default DefaultStart;

